import {
  Stack,
  Box,
  Text,
  Grid,
  Accordion,
  styled,
} from '@ltvco/refresh-lib/theme';
import { LoadingDots } from '@ltvco/refresh-lib/v1';
import {
  Gavel,
  Event,
  Timelapse,
  Assignment,
  Key,
  Group,
  Person,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';

import { CourtCaseDisclaimer } from '../components/CourtCaseDisclaimer';

import { useOralArgumentDetails } from './api';
import { formatDuration } from './components/OralArgumentsResultCard';

const StyledContentBox = styled(Box)(({ theme }) => ({
  maxHeight: 250,
  overflowY: 'auto',
  pr: 1,
  textAlign: 'left',
  '&::-webkit-scrollbar': {
    width: '8px',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: theme.palette.background.light,
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.text.secondary,
    borderRadius: '8px',
    border: `2px solid ${theme.palette.background.light}`,
  },
}));

interface InfoItemProps {
  show?: boolean;
  label: string;
  value: string | number | JSX.Element;
  icon: JSX.Element;
}

const InfoItem = ({ show = true, label, value, icon }: InfoItemProps) => {
  if (!show) {
    return null;
  }

  return (
    <Grid container size={{ xs: 12, md: 6 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon}
        <Text variant="body1">
          <Text variant="body1" fontWeight="600">
            {label}:
          </Text>{' '}
          {value}
        </Text>
      </Stack>
    </Grid>
  );
};
export const OralArgumentDetails = () => {
  const { id } = useParams();

  const result = useOralArgumentDetails(id);
  const { oralArgumentQuery, isLoading } = result;

  const {
    data: oralArgumentData,
    isError: isOralArgumentError,
    error: oralArgumentError,
  } = oralArgumentQuery;

  if (isLoading) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <LoadingDots count={5} />
      </Box>
    );
  }

  if (isOralArgumentError || !oralArgumentData) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <p>
          Error:{' '}
          {oralArgumentError instanceof Error
            ? oralArgumentError.message
            : 'Oral argument not found'}
        </p>
      </Box>
    );
  }

  // Extract data from API response
  const court = oralArgumentData.court;
  const docketNumber = oralArgumentData.docketNumber;
  const docketId = oralArgumentData.docket_id;
  const caseName = oralArgumentData.caseName;
  const arguedDate = oralArgumentData.dateArgued;

  return (
    <Box margin={3}>
      <Box sx={{ mb: 3 }}>
        <Text variant="h3" fontWeight="bold" sx={{ mb: 2 }}>
          Case Name: {caseName || 'Unknown Case'}
        </Text>

        {(court || docketNumber || docketId || arguedDate) && (
          <Box
            sx={(theme) => ({
              backgroundColor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              p: 2,
              boxShadow: 1,
              mb: 3,
            })}
          >
            <Grid container spacing={2}>
              {/* COURT */}
              <InfoItem
                show={!!court}
                label="Court"
                value={court || 'N/A'}
                icon={
                  <Gavel
                    sx={(theme) => ({
                      color: theme.palette.primary.main,
                      fontSize: '2rem',
                    })}
                  />
                }
              />

              {/* ARGUED DATE */}
              <InfoItem
                show={!!arguedDate}
                label="Argued Date"
                value={
                  arguedDate ? new Date(arguedDate).toLocaleDateString() : 'N/A'
                }
                icon={
                  <Event
                    sx={(theme) => ({
                      color: theme.palette.primary.main,
                      fontSize: '2rem',
                    })}
                  />
                }
              />

              {/* DURATION */}
              <InfoItem
                show={!!oralArgumentData.duration}
                label="Duration"
                value={
                  oralArgumentData.duration
                    ? formatDuration(oralArgumentData.duration)
                    : 'N/A'
                }
                icon={
                  <Timelapse
                    sx={(theme) => ({
                      color: theme.palette.primary.main,
                      fontSize: '2rem',
                    })}
                  />
                }
              />

              {/* DOCKET NUMBER */}
              <InfoItem
                show={!!docketNumber}
                label="Docket Number"
                value={docketNumber || 'N/A'}
                icon={
                  <Assignment
                    sx={(theme) => ({
                      color: theme.palette.primary.main,
                      fontSize: '2rem',
                    })}
                  />
                }
              />

              {/* DOCKET ID */}
              <InfoItem
                show={!!docketId}
                label="Docket ID"
                value={docketId || 'N/A'}
                icon={
                  <Key
                    sx={(theme) => ({
                      color: theme.palette.primary.main,
                      fontSize: '2rem',
                    })}
                  />
                }
              />
              {/* JUDGES */}
              <InfoItem
                show={!!(oralArgumentData.judges || oralArgumentData.judge)}
                label={`Judge${oralArgumentData.judges ? 's' : ''}`}
                value={oralArgumentData.judges || oralArgumentData.judge}
                icon={
                  oralArgumentData.judges ? (
                    <Group
                      sx={(theme) => ({
                        color: theme.palette.primary.main,
                        fontSize: '2rem',
                      })}
                    />
                  ) : (
                    <Person
                      sx={(theme) => ({
                        color: theme.palette.primary.main,
                        fontSize: '2rem',
                      })}
                    />
                  )
                }
              />
            </Grid>
          </Box>
        )}
        <Accordion
          display="Transcript"
          children={
            <StyledContentBox>
              <Text sx={{ lineHeight: 1.5, margin: 2, textAlign: 'left' }}>
                {oralArgumentData?.stt_transcript ||
                  'No transcription available.'}
              </Text>
            </StyledContentBox>
          }
        />
        {oralArgumentData?.download_url ? (
          <Box mb={3}>
            <audio controls style={{ width: '100%' }}>
              <source src={oralArgumentData.download_url} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </Box>
        ) : (
          <Text variant="body2" color="text.secondary">
            No audio available.
          </Text>
        )}
      </Box>
      <CourtCaseDisclaimer />
    </Box>
  );
};
